import { VENTANA_VENTAS_DIAS } from '../monetizacion/vendedorNiveles.js';
import { mlvApplyZ } from './mlvZLayers.js';
import { mlvSaveFocus } from './mlvFocusRestore.js';
import { mlvModalHistoryAttach } from '../navigation/mlvModalHistoryBridge.js';
import { mlvTrack } from '../telemetry/mlvTrack.js';
import { openNegocioVecinoExplainer } from './negocioVecinoExplainer.js';

function escHtml(s) {
  const d = document.createElement('div');
  d.textContent = s ?? '';
  return d.innerHTML;
}

const LS_CELEB_COUNT = 'mlv_celeb_venta_shown_count';
/** Primera vez: mensaje completo y más tiempo; desde la 2.ª venta registrada: modo compacto. */
const CELEB_AUTO_CLOSE_MS_FULL = 6000;
const CELEB_AUTO_CLOSE_MS_COMPACT = 4200;

/**
 * Overlay de feedback al marcar una venta.
 * @param {{ ventasDespues: number, progAntes: object, progDespues: object }} opts
 * @returns {Promise<void>}
 */
export function showVentaLogradaCelebracion({ ventasDespues, progAntes, progDespues }) {
  return new Promise((resolve) => {
    document.getElementById('mlv-venta-celeb')?.remove();

    let prevCount = 0;
    try {
      prevCount = Number(localStorage.getItem(LS_CELEB_COUNT) || '0') || 0;
    } catch (_e) {
      /* ignore */
    }
    try {
      localStorage.setItem(LS_CELEB_COUNT, String(prevCount + 1));
    } catch (_e) {
      /* ignore */
    }
    const compact = prevCount >= 1;
    const autoCloseMs = compact ? CELEB_AUTO_CLOSE_MS_COMPACT : CELEB_AUTO_CLOSE_MS_FULL;

    const restoreFocus = mlvSaveFocus();
    const subioNivel = Number(progDespues?.nivel) > Number(progAntes?.nivel);
    const labelEsc = escHtml(progDespues?.labelActual || '');
    const nivelLabelEsc = escHtml(progDespues?.labelActual || `Nivel ${progDespues?.nivel ?? ''}`);

    const wrap = document.createElement('div');
    wrap.id = 'mlv-venta-celeb';
    wrap.className =
      'fixed inset-0 flex items-center justify-center bg-black/55 p-4 backdrop-blur-[2px]';
    mlvApplyZ(wrap, 'ventaCelebracion');
    wrap.setAttribute('role', 'dialog');
    wrap.setAttribute('aria-modal', 'true');
    wrap.setAttribute('aria-labelledby', 'mlv-celeb-title');

    const pctA = Math.min(100, Math.max(0, Number(progAntes?.barPct) || 0));
    const pctD = Math.min(100, Math.max(0, Number(progDespues?.barPct) || 0));
    const metaTxt =
      progDespues?.siguienteUmbral != null
        ? ` · meta ${progDespues.siguienteUmbral} ventas`
        : '';

    const sparks = compact
      ? ''
      : `<div class="pointer-events-none absolute inset-0 overflow-hidden rounded-3xl" aria-hidden="true">
          ${[0, 1, 2, 3, 4, 5]
            .map(
              (i) =>
                `<span class="mlv-celeb-spark absolute bottom-1/3 h-2 w-2 rounded-full bg-brand-secondary" style="left:${12 + i * 14}%; animation-delay:${i * 0.08}s"></span>`
            )
            .join('')}
        </div>`;

    const cuerpo = compact
      ? `<p class="mt-2 text-sm leading-relaxed text-slate-600">
          Venta registrada. En tus últimos <strong class="text-slate-900">${VENTANA_VENTAS_DIAS} días</strong> llevás <strong class="text-brand-primary">${ventasDespues}</strong> venta(s) contadas para el nivel.
        </p>
        ${
          subioNivel
            ? `<p class="mt-2 rounded-xl bg-amber-100 px-3 py-2 text-sm font-bold text-amber-950 ring-1 ring-amber-200">¡Subiste a ${nivelLabelEsc}!</p>`
            : ''
        }`
      : `<p class="mt-2 text-sm leading-relaxed text-slate-600">
          Sumaste una venta en tu ventana de <strong class="text-slate-900">${VENTANA_VENTAS_DIAS} días</strong>: eso suma para tu <strong class="text-slate-900">nivel</strong> y define la <strong class="text-slate-900">comisión</strong> del marketplace.
        </p>
        <p class="mt-2 text-xs text-slate-500">
          Ventas contadas en la ventana: <span class="font-bold text-brand-primary">${ventasDespues}</span>
        </p>
        ${
          subioNivel
            ? `<p class="mt-3 rounded-xl bg-amber-100 px-3 py-2 text-sm font-bold text-amber-950 ring-1 ring-amber-200">
              ¡Subiste a ${nivelLabelEsc}!
            </p>`
            : `<p class="mt-2 text-[11px] text-slate-500">Seguí vendiendo en el barrio para avanzar de nivel.</p>`
        }`;

    wrap.innerHTML = `
      <div class="mlv-celeb-card relative w-full max-w-sm overflow-hidden rounded-3xl bg-white p-6 shadow-2xl ring-1 ring-slate-200/80">
        ${sparks}
        <div class="relative text-center">
          <div class="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-brand-secondary to-emerald-600 mlv-celeb-ring">
            <svg class="h-9 w-9 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>
          </div>
          <h2 id="mlv-celeb-title" class="font-heading text-xl font-bold text-slate-900" tabindex="-1">¡Venta registrada!</h2>
          ${cuerpo}
          <div class="mt-5 text-left">
            <p class="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">Progreso de nivel</p>
            <div class="h-3 w-full overflow-hidden rounded-full bg-slate-200">
              <div id="mlv-celeb-bar" class="h-full rounded-full bg-gradient-to-r from-brand-primary to-brand-secondary transition-[width] duration-1000 ease-out" style="width:${pctA}%"></div>
            </div>
            <p class="mt-1.5 text-[11px] text-slate-600">
              ${labelEsc}${metaTxt}
            </p>
          </div>
          <button type="button" id="mlv-celeb-ok" class="mt-6 w-full rounded-full bg-brand-primary py-3.5 text-sm font-bold text-white shadow-lg transition hover:brightness-95">
            Seguir
          </button>
          <button type="button" id="mlv-celeb-ver-reglas" class="mt-2 w-full rounded-full py-2 text-xs font-semibold text-brand-primary hover:underline">
            Ver nivel y comisión en detalle
          </button>
          <button type="button" id="mlv-celeb-close-quick" class="mt-1 w-full rounded-full py-2 text-xs font-semibold text-slate-600 underline decoration-slate-400 underline-offset-2 hover:text-slate-900">
            Cerrar sin esperar
          </button>
        </div>
      </div>
    `;

    let autoTimer = 0;
    let lastReason = 'unknown';
    const hist = mlvModalHistoryAttach(() => {
      if (autoTimer) {
        window.clearTimeout(autoTimer);
        autoTimer = 0;
      }
      document.removeEventListener('keydown', onKey);
      try {
        restoreFocus();
      } catch (_e) {
        /* ignore */
      }
      wrap.remove();
      mlvTrack('venta_celebracion_close', {
        reason: String(lastReason || 'unknown'),
        subioNivel,
        compact,
      });
      resolve();
    });
    function close(reason) {
      lastReason = String(reason || 'unknown');
      hist.closeFromUi();
    }

    function onKey(e) {
      if (e.key === 'Escape') close('escape');
    }

    document.body.appendChild(wrap);
    document.addEventListener('keydown', onKey);
    wrap.addEventListener('click', (e) => {
      if (e.target === wrap) close('backdrop');
    });
    wrap.querySelector('#mlv-celeb-ok').onclick = () => close('ok');
    wrap.querySelector('#mlv-celeb-close-quick').onclick = () => close('quick');
    wrap.querySelector('#mlv-celeb-ver-reglas').onclick = () => {
      close('explainer');
      queueMicrotask(() =>
        void openNegocioVecinoExplainer({ section: subioNivel ? 'comision' : 'nivel' })
      );
    };

    const titleEl = wrap.querySelector('#mlv-celeb-title');
    requestAnimationFrame(() => {
      titleEl?.focus?.({ preventScroll: true });
    });

    const bar = wrap.querySelector('#mlv-celeb-bar');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (bar) bar.style.width = `${pctD}%`;
      });
    });

    autoTimer = window.setTimeout(() => close('auto'), autoCloseMs);
  });
}

/**
 * Toast breve tras publicar.
 */
export function showPublicarListoToast({ durationMs = 4800 } = {}) {
  document.getElementById('mlv-pub-toast')?.remove();
  const el = document.createElement('div');
  el.id = 'mlv-pub-toast';
  el.className =
    'fixed bottom-24 left-1/2 w-[min(100%-2rem,22rem)] -translate-x-1/2 cursor-pointer rounded-2xl border border-brand-primary/20 bg-white px-4 py-3 text-sm text-slate-700 shadow-xl ring-1 ring-slate-200/80 mlv-toast-in';
  mlvApplyZ(el, 'pubListoToast');
  el.setAttribute('role', 'status');
  el.innerHTML = `
    <p class="font-heading font-bold text-brand-primary">¡Ya está en el barrio!</p>
    <p class="mt-1 text-xs leading-relaxed text-slate-600">
      Cuando lo vendas: <strong class="text-slate-800">Mis publicaciones</strong> → <strong class="text-slate-800">¡Lo vendí!</strong> (nivel y comisión según las reglas de la app).
    </p>
    <p class="mt-1 text-[10px] text-slate-400">Tocá para cerrar · Podés ver todo en perfil: «Cómo funciona el barrio»</p>
  `;
  document.body.appendChild(el);
  const t = window.setTimeout(() => {
    el.classList.add('mlv-toast-out');
    window.setTimeout(() => el.remove(), 320);
  }, durationMs);
  el.addEventListener('click', () => {
    window.clearTimeout(t);
    el.remove();
  });
}
